import type {
    ListProviderFacilitiesRequest,
    ListProviderProvidersRequest,
    ProviderFacility,
    ProviderProvider,
} from '../../api';
import { ProviderType } from '../../api';
import type { RosterApiPage } from './roster.api-page';
import { RosterSupportClients, SupportRosterDataSource } from './roster.support-source';
import {
    rosterFacilityFromProviderApp,
    rosterProviderAppFacilitiesRequest,
    rosterProviderAppProvidersRequest,
    rosterProviderAppSearches,
    rosterProviderAppSearchFilters,
    rosterProviderFromProviderApp,
} from './roster.provider-app-mapping';
import {
    ROSTER_API_MAX_LIMIT,
    rosterApiCanSort,
    rosterFacilityText,
    rosterMergeSearchPages,
    rosterProviderText,
} from './roster.support-mapping';
import type {
    RosterCapabilities,
    RosterFacility,
    RosterListQuery,
    RosterPage,
    RosterProvider,
    RosterRecord,
    RosterTab,
    RosterTypeCounts,
} from './roster.types';

/**
 * The provider app's endpoints the Roster uses, as plain functions — only the two lists. The app
 * wires them to the generated client (`createRosterProviderAppClients`); tests pass fakes.
 */
export interface RosterProviderAppClients {
    listProviders(request: ListProviderProvidersRequest): Promise<RosterApiPage<ProviderProvider>>;
    listFacilities(request: ListProviderFacilitiesRequest): Promise<RosterApiPage<ProviderFacility>>;
}

/**
 * What the Roster offers on the provider app. The provider API is read-only for it (probed
 * 2026-09-29: list and get only — no create, update, disable or tag route, and re-apply exists on
 * neither API). Those actions are still shown, disabled under a "Coming soon" hint (NS-4), and the
 * calls behind them stay wired, so when the backend adds a route it is one entry less in
 * `comingSoon`. The backend confirmed read-only is what is needed for now (2026-10-06).
 * `updatedTimestamp` is a field no endpoint returns, not an action.
 */
export const ROSTER_PROVIDER_APP_CAPABILITIES: RosterCapabilities = {
    write: true,
    applyToClaims: true,
    updatedTimestamp: false,
    tags: true,
    comingSoon: ['create', 'update', 'disable', 'tag', 'applyToClaims'],
};

/** The support-shaped write, tag and claims calls, pointed at the provider app's routes. */
export type RosterProviderAppWriteClients = Omit<RosterSupportClients, 'listProviders' | 'listFacilities'>;

/** Ids per `filterId` request — keeps the URL well under length limits. */
const IDS_PER_REQUEST = 50;

/**
 * The Roster on the PROVIDER app's API.
 *
 * Lists, search, counts and lookups by id are its own — the provider lists differ from support's
 * (no `filterActive`, no `filterFirstLast`, no tags on records). Saving, disabling, tagging and the
 * claims step are the support source's own logic, run against the provider routes by the clients
 * the app passes in (`createRosterProviderAppWriteClients`) — so both apps save, tag and re-apply
 * the same way, and differ only in where they send it.
 */
export class ProviderAppRosterDataSource extends SupportRosterDataSource {
    public readonly capabilities: RosterCapabilities = ROSTER_PROVIDER_APP_CAPABILITIES;

    constructor(
        private readonly lists: RosterProviderAppClients,
        writes: RosterProviderAppWriteClients,
    ) {
        super({
            ...writes,
            // The support-shaped lists are never used: every method that lists is overridden below.
            listProviders: () => Promise.reject(new Error('Use the provider-app list.')),
            listFacilities: () => Promise.reject(new Error('Use the provider-app list.')),
        });
    }

    public canSort(tab: RosterTab, key: string): boolean {
        return rosterApiCanSort(tab, key);
    }

    public async listProviders(query: RosterListQuery): Promise<RosterPage<RosterProvider>> {
        const request = rosterProviderAppProvidersRequest(query);
        const search = query.search?.trim();
        if (!search) {
            const page = await this.lists.listProviders(request);
            return { items: page.items.map(rosterProviderFromProviderApp), total: page.total };
        }
        const pages = await Promise.all(
            rosterProviderAppSearches('providers', search).map(async searchRequest => {
                const page = await this.lists.listProviders({
                    ...request,
                    offset: 0,
                    limit: ROSTER_API_MAX_LIMIT,
                    ...rosterProviderAppSearchFilters(searchRequest),
                });
                return { items: page.items.map(rosterProviderFromProviderApp), total: page.total };
            }),
        );
        return rosterMergeSearchPages(pages, query, rosterProviderText);
    }

    public async listFacilities(query: RosterListQuery): Promise<RosterPage<RosterFacility>> {
        const request = rosterProviderAppFacilitiesRequest(query);
        const search = query.search?.trim();
        if (!search) {
            const page = await this.lists.listFacilities(request);
            return { items: page.items.map(rosterFacilityFromProviderApp), total: page.total };
        }
        const pages = await Promise.all(
            rosterProviderAppSearches('facilities', search).map(async searchRequest => {
                const page = await this.lists.listFacilities({
                    ...request,
                    offset: 0,
                    limit: ROSTER_API_MAX_LIMIT,
                    ...rosterProviderAppSearchFilters(searchRequest),
                });
                return { items: page.items.map(rosterFacilityFromProviderApp), total: page.total };
            }),
        );
        return rosterMergeSearchPages(pages, query, rosterFacilityText);
    }

    public async listByIds(tab: RosterTab, ids: string[]): Promise<RosterRecord[]> {
        const records: RosterRecord[] = [];
        for (let i = 0; i < ids.length; i += IDS_PER_REQUEST) {
            const filterId = ids.slice(i, i + IDS_PER_REQUEST).map(id => `EQ:${id}`);
            if (tab === 'providers') {
                const page = await this.lists.listProviders({ filterId, limit: ROSTER_API_MAX_LIMIT });
                records.push(...page.items.map(rosterProviderFromProviderApp));
            } else {
                const page = await this.lists.listFacilities({ filterId, limit: ROSTER_API_MAX_LIMIT });
                records.push(...page.items.map(rosterFacilityFromProviderApp));
            }
        }
        return records;
    }

    /** One request per type — the API has no grouped count — plus the unfiltered total. */
    public async countProvidersByType(): Promise<RosterTypeCounts> {
        const types = Object.values(ProviderType) as ProviderType[];
        const [all, ...perType] = await Promise.all([
            this.lists.listProviders({ limit: 1 }),
            ...types.map(type => this.lists.listProviders({ filterProviderType: [`EQ:${type}`], limit: 1 })),
        ]);
        const byType: RosterTypeCounts['byType'] = {};
        types.forEach((type, index) => {
            byType[type] = perType[index].total;
        });
        return { total: all.total, byType };
    }
}
